import { Outlet, useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "../hooks/useReduxHooks";
import { useEffect, useRef, useState } from "react";
import {
  resetMobileSidebar,
  setMobileSidebar,
} from "../core/redux/sidebarSlice";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useBootstrapTooltips } from "../hooks/useBootstrapTooltips";
import { useMobileSidebarOverlay } from "../hooks/useMobileSidebarOverlay";
import type { RootState } from "../core/redux/store";
import type { SidebarState } from "../core/redux/sidebarSlice";
import ScrollToTop from "../components/scroll-to-top/ScrollToTop";
import { all_routes } from "../routes/all_routes";
import ThemeToggle from "../components/theme-toggle/themeToggle";
import {
  isDarkMode as themeIsDarkMode,
  toggleTheme as themeToggle,
  onThemeChange as onThemeChange,
} from "@/utils/theme";
import useCustomCursor from "@/components/custom-cursor/useCustomCursor";

const MainLayout = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isDarkMode, setIsDarkMode] = useState(() => themeIsDarkMode());
  const hideScrollToTopRoutes = [
    all_routes.home4,
    all_routes.home9,
    all_routes.home12,
  ];
  useCustomCursor();
  const hideThemeToggle =
    location.pathname === all_routes.home3 ||
    location.pathname === all_routes.home4 ||
    location.pathname === all_routes.home9 ||
    location.pathname === all_routes.home11;

  // Toggle theme function (delegates to shared util)
  const toggleTheme = () => {
    themeToggle();
  };

  // Keep local state in sync with the shared theme util
  useEffect(() => {
    const unsub = onThemeChange((isDark) => setIsDarkMode(isDark));
    return unsub;
  }, []);

  const { mobileSidebar } = useAppSelector(
    (state: RootState) => state.sidebar as SidebarState
  );

  // Ref for the main wrapper to detect clicks outside sidebar
  const mainWrapperRef = useRef<HTMLDivElement>(null);

  // Mobile detection
  const isMobile = useIsMobile();

  // Check if current page is layout-fullwidth
  const isLayoutFullwidth = location.pathname === "/layout-fullwidth";

  // Centralized tooltip initialization/cleanup
  useBootstrapTooltips([location.pathname]);

  // Centralized mobile sidebar overlay and outside click/touch handling
  useMobileSidebarOverlay({
    mobileSidebar,
    isLayoutFullwidth,
    isMobile,
    containerRef: mainWrapperRef,
    onClose: () => dispatch(setMobileSidebar(false)),
  });

  useEffect(() => {
    dispatch(resetMobileSidebar());
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const body = document.body;

    // Remove any existing theme-* classes to avoid stale theme remaining
    const existingThemeClasses = Array.from(body.classList).filter((c) =>
      /^theme-\d+$/.test(c)
    );
    existingThemeClasses.forEach((c) => body.classList.remove(c));

    // Determine theme for current route
    let themeToAdd = "";
    switch (location.pathname) {
      case all_routes.home2:
        themeToAdd = "theme-1";
        break;
      case all_routes.home3:
        themeToAdd = "theme-2";
        break;
      case all_routes.home4:
        themeToAdd = "theme-3";
        break;
      case all_routes.home5:
        themeToAdd = "theme-4";
        break;
      case all_routes.home6:
        themeToAdd = "theme-5";
        break;
      case all_routes.home7:
        themeToAdd = "theme-6";
        break;
      case all_routes.home8:
        body.classList.add("theme-7");
        break;
      case all_routes.home9:
        body.classList.add("theme-8");
        break;
      case all_routes.home10:
        body.classList.add("theme-9");
        break;
      case all_routes.home11:
        body.classList.add("theme-10");
        break;
      case all_routes.home12:
        body.classList.add("theme-11");
        break;
      case all_routes.home13:
        body.classList.add("theme-12");
        break;
      case all_routes.home14:
        body.classList.add("theme-13");
        break;
      case all_routes.home15:
        body.classList.add("theme-15");
        break;
      case all_routes.home16:
        body.classList.add("theme-16");
        break;
      case all_routes.home17:
        body.classList.add("theme-17");
        break;
      case all_routes.home18:
        body.classList.add("theme-18");
        break;

      default:
        themeToAdd = "";
        break;
    }

    if (themeToAdd) body.classList.add(themeToAdd);
  }, [location.pathname]);

  return (
    <>
      <div
        className={`main-wrapper ${
          location.pathname === all_routes.home3
            ? "home-three"
            : location.pathname === all_routes.home8
            ? "home-eight"
            : location.pathname === all_routes.home9
            ? "home-nine"
            : location.pathname === all_routes.home12
            ? "home-twelve"
            : location.pathname === all_routes.home13
            ? "home-thirteen"
            : ""
        }`}
        ref={mainWrapperRef}
      >
        {!hideThemeToggle && (
          <div className="header-theme header-theme-two">
            <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleTheme} />
          </div>
        )}

        <Outlet />
      </div>

      <div className={`sidebar-overlay${mobileSidebar ? " opened" : ""}`}></div>
      {/* ScrollToTop – hidden on index-4 & selected pages */}
      {!hideScrollToTopRoutes.includes(location.pathname) && <ScrollToTop />}
      <>
        {/* Cursor */}
        <div className="mouse-cursor cursor-outer" />
        <div className="mouse-cursor cursor-inner" />
        {/* /Cursor */}
      </>
    </>
  );
};

export default MainLayout;
